stimulusApp.register(
  "search--group-card",
  class extends Stimulus.Controller {
    static targets = [
      "summaryRow",
      "groupNumber",
      "adultsMinusButton",
      "childrenMinusButton",
      "adultsValue",
      "childrenValue",
      "agesList",
      "childAgeField",
      "childAgeSelector",
      "groupInput",
      "childrenNotice",
      "toggleButton",
      "ageSelectTemplate",
    ]

    static values = {
      i18n: Object,
      adults: { type: Number, default: 2 },
      children: { type: Number, default: 0 },
      opened: { type: Boolean, default: false },
      showValidation: { type: Boolean, default: false },
      title: String,
    }

    static classes = ["open"]

    connect() {
      if (!this.hasAgeSelectTemplateTarget) {
        console.error(
          "Age select template target is required for search/group_card_controller",
        )
      }

      this.#emitChange()
    }

    remove() {
      this.element.remove()
    }

    toggle() {
      if (this.element.parentElement?.children.length === 1) return
      this.openedValue = !this.openedValue
    }
    open() {
      this.openedValue = true
    }
    close() {
      this.openedValue = false
    }

    addAdult() {
      this.#changeAdults(1)
    }
    removeAdult() {
      this.#changeAdults(-1)
    }
    #changeAdults(delta) {
      this.adultsValue = Math.max(1, Math.min(10, this.adultsValue + delta))
    }

    addChild() {
      this.#changeChildren(1)
    }
    removeChild() {
      this.#changeChildren(-1)
    }
    #changeChildren(delta) {
      this.childrenValue = Math.max(0, Math.min(7, this.childrenValue + delta))
    }

    ageChange() {
      this.#refreshValidity()
      this.#notifyGroupChanged()
    }

    setIndex(index) {
      if (this.hasGroupNumberTarget) {
        this.groupNumberTarget.textContent = String(index + 1)
      }
    }

    setValidation(active) {
      this.showValidationValue = Boolean(active)
    }

    state() {
      return {
        adults: this.adultsValue,
        childrenAges: [...this.#childrenAges()],
        opened: this.openedValue,
      }
    }

    setState(adults, childrenAges) {
      this.adultsValue = adults
      this.childrenValue = childrenAges.length
      this.#updateChildren()
      const selectors = this.element.querySelectorAll(
        '[data-search--group-card-target~="childAgeSelector"]',
      )
      selectors.forEach((selector, index) => {
        selector.value = String(childrenAges[index])
      })
      return new Promise((resolve) => {
        queueMicrotask(() => {
          this.#notifyGroupChanged()
          resolve()
        })
      })
    }

    isValid() {
      if (this.adultsValue < 1) return false
      return this.#childrenAges().every((age) => age != null)
    }

    adultsValueChanged() {
      this.#renderAdults()
      this.#notifyGroupChanged()
    }

    childrenValueChanged() {
      this.#renderChildren()
      this.#notifyGroupChanged()
    }

    // The search page morphs on same-pathname visits, which keeps this element
    // (so no reconnect) and restores the server's markup, where the counts and
    // the summary render empty. Only the element's own morph event fires after
    // its whole subtree settled — descendants' events bubble up mid-morph.
    resync(event) {
      if (event.target !== this.element) return

      this.#renderAdults()
      this.#renderChildren()
      this.#renderOpenedState()
      this.#notifyGroupChanged()
    }

    #renderAdults() {
      if (this.hasAdultsValueTarget) {
        this.adultsValueTarget.textContent = String(this.adultsValue)
      }
      if (this.hasAdultsMinusButtonTarget) {
        this.#toggleMinusButton(
          this.adultsMinusButtonTarget,
          this.adultsValue <= 1,
        )
      }
    }

    #renderChildren() {
      this.#updateChildren()
      if (this.hasChildrenMinusButtonTarget) {
        this.#toggleMinusButton(
          this.childrenMinusButtonTarget,
          this.childrenValue <= 0,
        )
      }
    }

    openedValueChanged() {
      if (this.titleValue && this.opened) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ title: this.titleValue }),
        )
      }
      this.#renderOpenedState()
    }

    showValidationValueChanged() {
      this.#refreshValidity()
    }

    #groupSummary() {
      return HB.groupSummary({
        adults: this.adultsValue,
        childrenAges: this.#childrenAges(),
        i18n: this.i18nValue,
      })
    }

    #renderSummaryText() {
      if (!this.hasSummaryRowTarget) return
      this.summaryRowTarget.textContent = this.#groupSummary()
    }

    #renderGroupInput() {
      if (!this.hasGroupInputTarget) return
      this.groupInputTarget.value = [
        this.adultsValue,
        ...this.#childrenAges(),
      ].join(",")
    }

    #updateChildren() {
      if (this.hasChildrenValueTarget) {
        this.childrenValueTarget.textContent = String(this.childrenValue)
      }
      if (this.hasAgesListTarget) {
        while (this.childAgeFieldTargets.length > this.childrenValue) {
          this.childAgeFieldTargets.at(-1).remove()
        }
        while (this.childAgeFieldTargets.length < this.childrenValue) {
          const ageSelect = this.ageSelectTemplateTarget.content.cloneNode(true)
          if (ageSelect) {
            this.agesListTarget.appendChild(ageSelect)
          }
        }
      }
      this.#refreshValidity()
    }

    #renderOpenedState() {
      this.element.classList.toggle(this.openClass, this.openedValue)
      if (this.hasToggleButtonTarget) {
        this.toggleButtonTarget.classList.toggle(
          "animated-chevron--container_opened",
          this.openedValue,
        )
      }
    }

    #refreshValidity() {
      const ages = this.#childrenAges()
      const hasInvalidAges = ages.length > 0 && ages.some((age) => age == null)
      this.childAgeFieldTargets.forEach((field, index) => {
        const age = ages[index]
        const invalid = age == null
        field.classList.toggle(
          "field-container_error",
          invalid && this.showValidationValue,
        )
      })
      if (!this.hasChildrenNoticeTarget) return
      this.childrenNoticeTarget.classList.toggle("hidden", !hasInvalidAges)
      this.childrenNoticeTarget.classList.toggle(
        "search--children-notice_invalid",
        hasInvalidAges && this.showValidationValue,
      )
    }

    #notifyGroupChanged() {
      this.#renderSummaryText()
      this.#renderGroupInput()
      this.#emitChange()
    }

    #childrenAges() {
      return this.childAgeSelectorTargets.map((selector) => {
        const text = selector.value.trim()
        if (text === "") return null
        const parsed = Number(text)
        return Number.isNaN(parsed) ? null : parsed
      })
    }

    #toggleMinusButton(target, disabled) {
      target.disabled = disabled
      target.setAttribute("aria-disabled", String(disabled))
    }

    #emitChange() {
      this.element.dispatchEvent(
        new CustomEvent("hb:group-card:changed", {
          bubbles: true,
          detail: { card: this.element },
        }),
      )
    }
  },
)
